import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GripVertical } from 'lucide-react';
import { CommunityPageSectionRow } from '@/types/cms';

interface SectionListProps {
  sections: CommunityPageSectionRow[];
  onSectionReorder: (sections: CommunityPageSectionRow[]) => void;
  editingSection: string | null;
  onEditSection: (sectionId: string | null) => void;
  renderSectionEditor: (section: CommunityPageSectionRow) => React.ReactNode;
}

interface SortableItemProps {
  section: CommunityPageSectionRow;
  index: number;
  editingSection: string | null;
  onEditSection: (sectionId: string | null) => void;
  editContent?: React.ReactNode;
}

const SortableItem: React.FC<SortableItemProps> = ({ 
  section, 
  index, 
  editingSection, 
  onEditSection,
  editContent
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isEditing = editingSection === section.id;

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'opacity-50' : ''}>
      <Card className={`border bg-white transition-all duration-200 ${
        isEditing 
          ? 'border-primary/30 shadow-md' 
          : 'border-gray-200 hover:border-gray-300'
      }`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100 transition-colors"
              >
                <GripVertical className="h-4 w-4 text-gray-400" />
              </div>
              <div>
                <CardTitle className="text-sm font-medium">
                  {section.title || `${section.section_type} Section`}
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Section {index + 1} • {section.section_type}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={isEditing ? "default" : "outline"}
                size="sm"
                onClick={() => onEditSection(isEditing ? null : section.id)}
              >
                {isEditing ? 'Close' : 'Edit'}
              </Button>
            </div>
          </div>
        </CardHeader>
        
        {isEditing && editContent && (
          <CardContent className="pt-0 border-t bg-gray-50/50">
            {editContent}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

const SectionList: React.FC<SectionListProps> = ({
  sections,
  onSectionReorder,
  editingSection,
  onEditSection,
  renderSectionEditor
}) => {
  const [activeId, setActiveId] = React.useState<UniqueIdentifier | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = sections.findIndex((section) => section.id === active.id);
      const newIndex = sections.findIndex((section) => section.id === over?.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newSections = arrayMove(sections, oldIndex, newIndex);
        // Update display_order for all sections
        const updatedSections = newSections.map((section, index) => ({
          ...section,
          display_order: index + 1
        }));
        onSectionReorder(updatedSections);
      }
    }
    
    setActiveId(null);
  };

  const activeSection = sections.find(section => section.id === activeId);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-4">
          {sections.map((section, index) => (
            <SortableItem
              key={section.id}
              section={section}
              index={index}
              editingSection={editingSection}
              onEditSection={onEditSection}
              editContent={editingSection === section.id ? renderSectionEditor(section) : undefined}
            />
          ))}
        </div>
      </SortableContext>
      
      <DragOverlay>
        {activeSection ? (
          <Card className="border border-gray-200 bg-white shadow-lg">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-1 rounded">
                    <GripVertical className="h-4 w-4 text-gray-400" />
                  </div>
                  <div>
                    <CardTitle className="text-sm font-medium">
                      {activeSection.title || `${activeSection.section_type} Section`}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">
                      {activeSection.section_type}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled>
                    Edit
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default SectionList;